// Kitchen unit conversion: pure functions, no I/O. US customary volume units
// (the cup is the US customary cup, 236.59 ml, not the 240 ml "legal" cup).

export type VolumeUnit = "tsp" | "tbsp" | "floz" | "cup" | "ml" | "l";
export type WeightUnit = "g" | "kg" | "oz" | "lb";
export type TempUnit = "F" | "C";
export type Unit = VolumeUnit | WeightUnit | TempUnit;

export const VOLUME_UNITS: readonly VolumeUnit[] = ["tsp", "tbsp", "floz", "cup", "ml", "l"];
export const WEIGHT_UNITS: readonly WeightUnit[] = ["oz", "lb", "g", "kg"];
export const TEMP_UNITS: readonly TempUnit[] = ["F", "C"];

/** Millilitres per unit. */
export const ML_PER: Record<VolumeUnit, number> = {
  tsp: 4.92892159375,
  tbsp: 14.78676478125,
  floz: 29.5735295625,
  cup: 236.5882365,
  ml: 1,
  l: 1000,
};

/** Grams per unit. */
export const G_PER: Record<WeightUnit, number> = {
  g: 1,
  kg: 1000,
  oz: 28.349523125,
  lb: 453.59237,
};

export const UNIT_LABEL: Record<Unit, { short: string; one: string; many: string }> = {
  tsp: { short: "tsp", one: "teaspoon", many: "teaspoons" },
  tbsp: { short: "tbsp", one: "tablespoon", many: "tablespoons" },
  floz: { short: "fl oz", one: "fluid ounce", many: "fluid ounces" },
  cup: { short: "cup", one: "cup", many: "cups" },
  ml: { short: "ml", one: "millilitre", many: "millilitres" },
  l: { short: "l", one: "litre", many: "litres" },
  g: { short: "g", one: "gram", many: "grams" },
  kg: { short: "kg", one: "kilogram", many: "kilograms" },
  oz: { short: "oz", one: "ounce", many: "ounces" },
  lb: { short: "lb", one: "pound", many: "pounds" },
  F: { short: "°F", one: "°F", many: "°F" },
  C: { short: "°C", one: "°C", many: "°C" },
};

export function isVolume(u: Unit): u is VolumeUnit {
  return u in ML_PER;
}
export function isWeight(u: Unit): u is WeightUnit {
  return u in G_PER;
}
export function isTemp(u: Unit): u is TempUnit {
  return u === "F" || u === "C";
}

export type IngredientKey = "flour" | "sugar" | "brownSugar" | "butter" | "rice" | "oats" | "honey";

/**
 * Grams per US cup. Approximate: widely published baking-chart figures
 * (e.g. King Arthur's ingredient weight chart, USDA). Actual weight varies
 * with how the cup is filled; flour is spooned and levelled, brown sugar packed.
 */
export const INGREDIENTS: Record<IngredientKey, { label: string; gramsPerCup: number; note?: string }> = {
  flour: { label: "Flour", gramsPerCup: 120, note: "all-purpose, spooned & levelled" },
  sugar: { label: "Sugar", gramsPerCup: 200, note: "granulated" },
  brownSugar: { label: "Brown sugar", gramsPerCup: 213, note: "packed" },
  butter: { label: "Butter", gramsPerCup: 227, note: "1 stick = ½ cup = 113 g" },
  rice: { label: "Rice", gramsPerCup: 185, note: "uncooked long-grain" },
  oats: { label: "Oats", gramsPerCup: 90, note: "rolled" },
  honey: { label: "Honey", gramsPerCup: 340 },
};
export const INGREDIENT_KEYS = Object.keys(INGREDIENTS) as IngredientKey[];

export function fToC(f: number): number {
  return ((f - 32) * 5) / 9;
}
export function cToF(c: number): number {
  return (c * 9) / 5 + 32;
}

/**
 * Convert between any two units. Volume↔weight needs an ingredient (its
 * density); temperature only converts to temperature. Returns null when the
 * conversion is not meaningful.
 */
export function convert(value: number, from: Unit, to: Unit, ingredient?: IngredientKey): number | null {
  if (!Number.isFinite(value)) return null;
  if (from === to) return value;
  if (isTemp(from) || isTemp(to)) {
    if (!isTemp(from) || !isTemp(to)) return null;
    return from === "F" ? fToC(value) : cToF(value);
  }
  if (isVolume(from) && isVolume(to)) return (value * ML_PER[from]) / ML_PER[to];
  if (isWeight(from) && isWeight(to)) return (value * G_PER[from]) / G_PER[to];
  if (!ingredient) return null;
  const gPerMl = INGREDIENTS[ingredient].gramsPerCup / ML_PER.cup;
  if (isVolume(from) && isWeight(to)) return (value * ML_PER[from] * gPerMl) / G_PER[to];
  if (isWeight(from) && isVolume(to)) return (value * G_PER[from]) / gPerMl / ML_PER[to];
  return null;
}

// ---------- Oven marks ----------

export interface OvenMark {
  gas: number;
  f: number;
  c: number;
  label: string;
}

/** UK gas marks with their conventional °F/°C equivalents (rounded as printed in recipes). */
export const OVEN_MARKS: readonly OvenMark[] = [
  { gas: 1, f: 275, c: 140, label: "Very low" },
  { gas: 2, f: 300, c: 150, label: "Low" },
  { gas: 3, f: 325, c: 170, label: "Moderately low" },
  { gas: 4, f: 350, c: 180, label: "Moderate" },
  { gas: 5, f: 375, c: 190, label: "Moderately hot" },
  { gas: 6, f: 400, c: 200, label: "Hot" },
  { gas: 7, f: 425, c: 220, label: "Hot" },
  { gas: 8, f: 450, c: 230, label: "Very hot" },
  { gas: 9, f: 475, c: 240, label: "Very hot" },
];

/** The oven mark closest to a temperature, if it is within the oven range (±12 °F). */
export function nearestOvenMark(value: number, unit: TempUnit): OvenMark | null {
  const f = unit === "F" ? value : cToF(value);
  let best: OvenMark | null = null;
  for (const m of OVEN_MARKS) if (!best || Math.abs(m.f - f) < Math.abs(best.f - f)) best = m;
  return best && Math.abs(best.f - f) <= 12 ? best : null;
}

// ---------- Display ----------

const FRACTIONS: { v: number; s: string }[] = [
  { v: 0, s: "" },
  { v: 1 / 8, s: "⅛" },
  { v: 1 / 4, s: "¼" },
  { v: 1 / 3, s: "⅓" },
  { v: 3 / 8, s: "⅜" },
  { v: 1 / 2, s: "½" },
  { v: 5 / 8, s: "⅝" },
  { v: 2 / 3, s: "⅔" },
  { v: 3 / 4, s: "¾" },
  { v: 7 / 8, s: "⅞" },
  { v: 1, s: "" },
];

export interface FormattedAmount {
  /** Number text, e.g. "1 ½", "250", "0.35". */
  text: string;
  /** True when the text is rounded noticeably (more than ~2%) from the exact value. */
  approx: boolean;
}

/** Fixed decimals without trailing fractional zeros: 180 → "180", 2.50 → "2.5". */
function trimNumber(n: number, decimals: number): string {
  const s = n.toFixed(decimals);
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
}

/**
 * Kitchen-friendly number formatting: cooking fractions for US spoons and
 * cups, sensible rounding for everything else.
 */
export function formatAmount(value: number, unit: Unit): FormattedAmount {
  if (!Number.isFinite(value)) return { text: "–", approx: false };
  if (isTemp(unit)) return { text: String(Math.round(value)), approx: Math.abs(Math.round(value) - value) > 0.5 };
  const abs = Math.abs(value);
  if (abs === 0) return { text: "0", approx: false };
  const fractional = unit === "tsp" || unit === "tbsp" || unit === "cup";
  if (fractional && abs < 20) {
    const whole = Math.floor(abs);
    const rest = abs - whole;
    let best = FRACTIONS[0]!;
    for (const f of FRACTIONS) if (Math.abs(f.v - rest) < Math.abs(best.v - rest)) best = f;
    const w = whole + (best.v === 1 ? 1 : 0);
    const rounded = w + (best.v === 1 ? 0 : best.v);
    if (rounded > 0) {
      const text = w && best.s ? `${w} ${best.s}` : best.s || String(w);
      return { text: (value < 0 ? "-" : "") + text, approx: Math.abs(rounded - abs) / abs > 0.02 };
    }
  }
  let decimals: number;
  if (abs >= 100) decimals = 0;
  else if (abs >= 10) decimals = unit === "g" || unit === "ml" ? 0 : 1;
  else if (abs >= 1) decimals = unit === "g" || unit === "ml" ? 1 : 2;
  else decimals = unit === "kg" || unit === "l" || unit === "lb" ? 3 : 2;
  const text = trimNumber(value, decimals);
  const shown = Number(text);
  return { text: text === "0" || text === "-0" ? "< 0.01" : text, approx: shown === 0 || Math.abs(shown - value) / abs > 0.02 };
}

export function unitLabel(unit: Unit, value: number): string {
  if (unit === "cup") return Math.abs(value) > 1 ? "cups" : "cup";
  return UNIT_LABEL[unit].short;
}

/** Default step for the +/− stepper, chosen per unit. */
export function stepFor(unit: Unit): number {
  switch (unit) {
    case "tsp":
    case "tbsp":
      return 0.5;
    case "cup":
      return 0.25;
    case "floz":
    case "oz":
      return 1;
    case "lb":
      return 0.25;
    case "kg":
    case "l":
      return 0.1;
    case "ml":
      return 25;
    case "g":
      return 10;
    case "F":
      return 25;
    case "C":
      return 10;
  }
}
